'use client'
import { cn, handleUETClick } from '@/lib/utils'
import { motion } from 'framer-motion'
import Image from 'next/image'
import Link from 'next/link'
import React, { useRef, useState } from 'react'
import { useSearchParams } from 'next/navigation'

interface IFeature {
  title: string
  description: string
  icon: React.ReactNode
  cover: string
  video: string
  link: string
  className?: string
}

export function Features() {
  return (
    <div className="mx-auto mt-16 w-full max-w-7xl px-4 md:px-8">
      {/* <Heading as="h1" className="relative z-10 mx-auto mt-6 max-w-6xl text-center text-4xl font-bold text-foreground md:text-4xl lg:text-6xl">
        AI智绘矩阵，创作无限可能
      </Heading>
      <Subheading className="text-muted relative z-10 mx-auto max-w-7xl text-center text-base text-foreground md:text-xl">
        跨越传统图表工具的局限，融合AI智能与设计美学，让数据故事更具表现力
      </Subheading> */}
      <div className="cols-1 grid gap-4 md:auto-rows-[18rem] md:grid-cols-6 lg:auto-rows-[22rem] xl:auto-rows-[25rem]">
        {items.map((item, index) => (
          <FeatureCard key={index} {...item} />
        ))}
      </div>
    </div>
  )
}

const FeatureCard = (props: IFeature) => {
  const [isHovered, setIsHovered] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)

  // Handle hover state and video control
  const handleMouseEnter = () => {
    setIsHovered(true)
    const video = videoRef.current
    if (video) {
      video.currentTime = 0
      const playPromise = video.play()
      if (playPromise !== undefined) {
        playPromise.catch(err => {
          // AbortError: play() was interrupted by pause() - safe to ignore
          if (err?.name !== 'AbortError') throw err
        })
      }
    }
  }

  const handleMouseLeave = () => {
    setIsHovered(false)
    const video = videoRef.current
    if (video) {
      video.pause()
      video.currentTime = 0
    }
  }

  return (
    <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} className={cn('group relative flex flex-col justify-between', props.className)}>
      <Card className="relative overflow-hidden rounded-5">
        <CardSkeletonBody className="relative h-full w-full flex-1">
          <motion.div initial={false} animate={{ opacity: isHovered ? 0 : 1 }} transition={{ duration: 0.3 }} className="h-full w-full">
            <Image src={props.cover} alt={props.title} width={500} height={500} className="h-full w-full rounded-lg object-cover" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: isHovered ? 1 : 0 }}
            transition={{ duration: 0.3 }}
            className="absolute inset-0 overflow-hidden rounded-3xl"
          >
            <video ref={videoRef} src={props.video} className="h-full w-full rounded-lg object-cover" playsInline muted />
          </motion.div>
        </CardSkeletonBody>
        <CardContent className="absolute bottom-0 left-0 w-full space-y-2.5 bg-gradient-to-t from-white to-white/0 p-4 backdrop-blur-1.5">
          <CardIcon>{props.icon}</CardIcon>
          <CardTitle>{props.title}</CardTitle>
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: isHovered ? 1 : 0, height: isHovered ? 'auto' : 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <CardDescription link={props.link}>{props.description}</CardDescription>
          </motion.div>
        </CardContent>
      </Card>
    </div>
  )
}

const items: IFeature[] = [
  {
    title: 'AI数据报告',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28" fill="none">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M0.0151062 4.99999C0.0151062 2.23857 2.25368 0 5.0151 0H22.9849C25.7463 0 27.9849 2.23857 27.9849 4.99999V13.2014C27.9849 13.7536 27.5373 14.2014 26.9849 14.2014C26.4327 14.2014 25.9849 13.7536 25.9849 13.2014V4.99999C25.9849 3.34315 24.6419 1.99999 22.9849 1.99999H5.0151C3.35824 1.99999 2.01511 3.34315 2.01511 4.99999V17.6012L6.0933 12.909C6.87838 12.0057 8.2757 11.9895 9.08152 12.8743L14.2586 18.559C14.6304 18.9674 14.6009 19.5998 14.1926 19.9718C13.7842 20.3436 13.1518 20.314 12.7799 19.9056L7.60284 14.221L2.01511 20.65V23C2.01511 24.6568 3.35824 26 5.0151 26H11.0035C11.5558 26 12.0035 26.4478 12.0035 27C12.0035 27.5524 11.5558 28 11.0035 28H5.0151C2.25368 28 0.0151062 25.7614 0.0151062 23V4.99999ZM17.9658 7.92401C17.6936 7.99359 17.5859 8.32249 17.7642 8.53991L19.0518 9.38364V11.0861C19.1213 11.3588 19.4505 11.4668 19.6678 11.2883L20.5092 9.99954H22.2112C22.4834 9.92996 22.5912 9.60106 22.4129 9.38364L21.1252 8.30478V6.83746C21.0557 6.56477 20.7264 6.45669 20.5092 6.63525L19.6678 7.92401H17.9658Z"
          fill="#464A5E"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M22.7296 27.2783L21.5312 23.9273H17.6955L16.4865 27.2809C16.331 27.7123 15.9217 27.9997 15.4632 27.9997C14.7039 27.9997 14.1782 27.2413 14.4447 26.5303L18.0828 16.8216C18.3221 16.1829 18.9326 15.7598 19.6147 15.7598C20.2974 15.7598 20.9084 16.1838 21.1472 16.8234L24.7726 26.5317C25.038 27.2425 24.5124 27.9997 23.7536 27.9997C23.2942 27.9997 22.8842 27.7109 22.7296 27.2783ZM19.6633 18.7197L20.8152 21.9273H18.4156L19.5674 18.7197H19.6633ZM28 17.1685C28 16.6172 27.5532 16.1701 27.0018 16.1697C26.45 16.1693 26.0024 16.6166 26.0024 17.1686V26.8905C26.0024 27.4419 26.4492 27.8889 27.0006 27.8893C27.5526 27.8897 28.0002 27.4423 28.0002 26.8905L28 17.1685Z"
          fill="#464A5E"
        />
      </svg>
    ),
    description: '快速生成专业报告内容，让数据分析更完整，表达更有说服力',
    cover: 'https://cdn.aitubiao.com/static/images/itubiao/landingpage/hero-data-report.jpg',
    video: 'https://cdn.aitubiao.com/static/videos/hero-data-report.mp4',
    className: 'md:col-span-3',
    link: 'https://app.aitubiao.com/home/ai'
  },
  {
    title: '数据大屏',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
        <path
          d="M13.2687 2.26901C15.9845 1.72882 18.7994 2.00607 21.3576 3.06569C23.9157 4.12532 26.1022 5.91973 27.6406 8.22202C29.1789 10.5243 30 13.2311 30 16C30 16.5523 29.5523 17 29 17C28.4477 17 28 16.5523 28 16C28 13.6266 27.2962 11.3066 25.9776 9.33316C24.6591 7.35977 22.7849 5.8217 20.5922 4.91345C18.3995 4.0052 15.9867 3.76756 13.6589 4.23058C11.3312 4.69361 9.19296 5.83649 7.51472 7.51472C5.83649 9.19296 4.69361 11.3312 4.23058 13.6589C3.76756 15.9867 4.0052 18.3995 4.91345 20.5922C5.8217 22.7849 7.35977 24.6591 9.33316 25.9776C11.0283 27.1103 12.9792 27.7893 15 27.9583V25.9499C13.3755 25.7866 11.8102 25.2274 10.4443 24.3147C8.79981 23.2159 7.51809 21.6541 6.76121 19.8268C6.00433 17.9996 5.8063 15.9889 6.19215 14.0491C6.578 12.1093 7.53041 10.3275 8.92894 8.92894C10.3275 7.53041 12.1093 6.578 14.0491 6.19215C15.9889 5.8063 17.9996 6.00433 19.8268 6.76121C21.6541 7.51809 23.2159 8.79981 24.3147 10.4443C25.4135 12.0888 26 14.0222 26 16C26 16.5523 25.5523 17 25 17C24.4477 17 24 16.5523 24 16C24 14.4178 23.5308 12.871 22.6518 11.5554C21.7727 10.2398 20.5233 9.21447 19.0615 8.60897C17.5997 8.00347 15.9911 7.84504 14.4393 8.15372C12.8874 8.4624 11.462 9.22433 10.3432 10.3432C9.22433 11.462 8.4624 12.8874 8.15372 14.4393C7.84504 15.9911 8.00347 17.5997 8.60897 19.0615C9.21447 20.5233 10.2398 21.7727 11.5554 22.6518C12.592 23.3444 13.7721 23.7826 15 23.9373V21.9161C14.1692 21.7756 13.3734 21.4611 12.6666 20.9888C11.6799 20.3295 10.9109 19.3925 10.4567 18.2961C10.0026 17.1997 9.88378 15.9933 10.1153 14.8295C10.3468 13.6656 10.9182 12.5965 11.7574 11.7574C12.5965 10.9182 13.6656 10.3468 14.8295 10.1153C15.9933 9.88378 17.1997 10.0026 18.2961 10.4567C19.3925 10.9109 20.3295 11.6799 20.9888 12.6666C21.6481 13.6533 22 14.8133 22 16C22 16.5523 21.5523 17 21 17C20.4477 17 20 16.5523 20 16C20 15.2089 19.7654 14.4355 19.3259 13.7777C18.8864 13.1199 18.2616 12.6072 17.5307 12.3045C16.7998 12.0017 15.9956 11.9225 15.2196 12.0769C14.4437 12.2312 13.731 12.6122 13.1716 13.1716C12.6122 13.731 12.2312 14.4437 12.0769 15.2196C11.9225 15.9956 12.0017 16.7998 12.3045 17.5307C12.6072 18.2616 13.1199 18.8864 13.7777 19.3259C14.1535 19.577 14.567 19.7612 15 19.873V17.7324C14.4022 17.3866 14 16.7403 14 16C14 14.8954 14.8954 14 16 14C17.1046 14 18 14.8954 18 16C18 16.7403 17.5978 17.3866 17 17.7324V20.9976L17 21L17 21.0024V24.9969L17 25L17 25.0031V28.9963L17 29L17 29.0037V31C17 31.5523 16.5523 32 16 32C15.4477 32 15 31.5523 15 31V29.9643C12.5831 29.7912 10.2462 28.9931 8.22202 27.6406C5.91973 26.1022 4.12532 23.9157 3.06569 21.3576C2.00607 18.7994 1.72882 15.9845 2.26901 13.2687C2.80921 10.553 4.14258 8.05845 6.10051 6.10051C8.05845 4.14258 10.553 2.80921 13.2687 2.26901Z"
          fill="#464A5E"
        />
      </svg>
    ),
    description: '轻松制作高颜值数据大屏，让关键指标展示更直观、更有冲击力',
    cover: 'https://cdn.aitubiao.com/static/images/itubiao/landingpage/hero-bigscreen.png',
    video: 'https://cdn.aitubiao.com/static/videos/hero-bigscreen.mp4',
    className: 'md:col-span-3',
    link: 'https://app.aitubiao.com/home/ai?tab=bigScreen'
  },
  {
    title: '经典图表',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M31.634 0.226666C31.8657 0.416605 32 0.700415 32 1.00001V29C32 29.4767 31.6635 29.8871 31.1961 29.9806L21.1961 31.9806C21.0667 32.0065 20.9333 32.0065 20.8039 31.9806L11 30.0198L1.19612 31.9806C0.902341 32.0393 0.597699 31.9633 0.366011 31.7734C0.134323 31.5834 0 31.2996 0 31V3.00001C0 2.52333 0.33646 2.11291 0.803884 2.01943L10.8039 0.0194279C10.9333 -0.00646446 11.0667 -0.00646446 11.1961 0.0194279L21 1.9802L30.8039 0.0194279C31.0977 -0.0393271 31.4023 0.0367278 31.634 0.226666ZM20 3.81981L12 2.21981V28.1802L20 29.7802V3.81981ZM22 29.7802L30 28.1802V2.21981L22 3.81981V29.7802ZM10 28.1802V2.21981L2 3.81981V29.7802L10 28.1802Z"
          fill="#464A5E"
        />
      </svg>
    ),
    description: '多样化专业图表模型，轻松应对日常分析与汇报',
    cover: 'https://cdn.aitubiao.com/static/images/itubiao/landingpage/hero-chart.jpg',
    video: 'https://cdn.aitubiao.com/static/videos/hero-chart.mp4',
    className: 'md:col-span-2',
    link: 'https://app.aitubiao.com/home/ai?tab=classicChart'
  },
  {
    title: '数据地图',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
        <path
          d="M20.9894 13.8385L23.5459 12.6028C24.1512 12.3103 24.2805 11.5045 23.7971 11.0372C23.2932 10.5501 22.4342 10.7603 21.9343 11.2514C21.4741 11.7036 20.9072 11.9988 20.3027 12.3136L20.0972 12.421C19.422 12.7755 19.5698 13.6721 20.2998 13.8927C20.5283 13.9617 20.7745 13.9423 20.9894 13.8385Z"
          fill="#464A5E"
        />
        <path
          d="M15.4828 19.6154V19.5602C15.4828 19.222 15.6891 18.918 16.0034 18.7932C16.7061 18.514 17.3831 19.2481 17.0481 19.926L16.6098 20.8127C16.5486 20.9366 16.4664 21.0491 16.367 21.1452L15.9825 21.5169C15.7038 21.7863 15.2617 21.7863 14.983 21.5169C14.6907 21.2343 14.6907 20.7657 14.983 20.4831L15.2062 20.2673C15.383 20.0965 15.4828 19.8612 15.4828 19.6154Z"
          fill="#464A5E"
        />
        <path
          d="M11.3448 25L12.3073 25.4652C12.8192 25.7126 13.4138 25.3396 13.4138 24.771C13.4138 24.3452 13.0686 24 12.6428 24H11.8092C11.5077 24 11.2142 23.9028 10.9722 23.7229L10.4182 23.311C10.178 23.1324 9.84314 23.1569 9.63146 23.3685C9.32926 23.6707 9.42832 24.1827 9.82144 24.3503L11.3448 25Z"
          fill="#464A5E"
        />
        <path
          d="M16 0C7.16344 0 0 7.16344 0 16C0 24.8366 7.16344 32 16 32C24.8366 32 32 24.8366 32 16C32 7.16344 24.8366 0 16 0ZM3.22457 21.7346L4.73499 19.158C5.38338 18.0519 6.89041 17.833 7.8267 18.7089L9.97472 20.7183C10.7423 21.4363 11.938 21.4266 12.6938 20.696L12.7699 20.6225C13.1746 20.2312 13.3683 19.6704 13.2914 19.1128C13.1402 18.0162 14.0296 17.0452 15.1289 16.9147C16.5859 16.7417 18.3038 16.2664 18.5862 15C18.7584 14.2281 18.3573 13.3415 17.8637 12.5995C17.449 11.9761 17.8634 11 18.6121 11C18.8578 11 19.0907 10.8904 19.2473 10.7012L20.0336 9.75108C20.4259 9.27702 21.016 9.01692 21.6272 8.94617C22.7361 8.81784 24.4417 8.39279 24.7931 7C24.9906 6.21705 24.4107 5.73145 23.8257 5.24162C23.3174 4.81599 22.8053 4.38716 22.7961 3.75728C27.0929 6.14759 30 10.7343 30 16C30 17.08 29.8777 18.1314 29.6462 19.1412C29.5909 19.0965 29.5326 19.0544 29.4716 19.015L28.8399 18.6079C28.254 18.2303 27.5137 18.185 26.8861 18.4884L26.5488 18.6514C26.0859 18.8752 25.5545 18.912 25.0651 18.7543L23.0233 18.0964C22.8251 18.0325 22.6181 18 22.4099 18H22.0811C21.2278 18 20.4685 18.5414 20.1903 19.348L19.8463 20.3457C19.7001 20.7696 19.4158 21.132 19.039 21.3749L17.4338 22.4093C16.8625 22.7775 16.5172 23.4107 16.5172 24.0904V25C16.5172 26.1046 17.4127 27 18.5172 27H19.0321C19.4163 27 19.7925 27.1107 20.1155 27.3189L21.6407 28.3018C21.8182 28.4161 22.0092 28.4997 22.2064 28.5526C20.336 29.4792 18.2288 30 16 30C10.3113 30 5.41511 26.6071 3.22457 21.7346Z"
          fill="#464A5E"
        />
      </svg>
    ),
    description: '直观呈现地理维度价值，洞察分布趋势与区域差异',
    cover: 'https://cdn.aitubiao.com/static/images/itubiao/landingpage/hero-data-map.png',
    video: 'https://cdn.aitubiao.com/static/videos/new-data-map.mp4',
    className: 'md:col-span-2',
    link: 'https://app.aitubiao.com/home/template?scope=chart&tagIds=ceranm66tb8r38pvckat97ycv'
  },
  {
    title: '3D图表',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M7.37167 3.54126C7.25165 3.11434 6.64646 3.11434 6.52644 3.54126L6.18998 4.73808C5.70325 6.46943 4.31142 7.79777 2.55925 8.20319C2.26679 8.27087 2.2343 8.67438 2.51217 8.78797L2.68388 8.85816C4.39408 9.55727 5.67515 11.021 6.14148 12.8087L6.4471 13.9804C6.5659 14.4358 7.22061 14.4111 7.3048 13.9481L7.41746 13.3284C7.78871 11.2866 9.20246 9.58838 11.1432 8.85311L11.3453 8.77654C11.6266 8.66998 11.5991 8.26342 11.3061 8.19562C9.57413 7.79486 8.19835 6.48184 7.71723 4.77045L7.37167 3.54126ZM16.9359 5.1156C17.2026 4.15499 18.5568 4.13337 18.854 5.08498L20.0578 8.93975C20.7501 11.1565 22.3715 12.963 24.5008 13.89L26.7156 14.8542C27.5224 15.2054 27.515 16.3523 26.7038 16.693L24.3336 17.6887C22.0885 18.6318 20.3952 20.5462 19.7332 22.8897L18.296 27.9775C18.1612 28.4548 17.4886 28.4657 17.3383 27.993L15.6572 22.7028C14.9472 20.4685 13.2928 18.658 11.1313 17.75L8.71501 16.735C7.88947 16.3882 7.90083 15.2145 8.73293 14.8838L11.2143 13.8975C13.5396 12.9732 15.2984 11.0142 15.9678 8.60312L16.9359 5.1156ZM24.7922 21.1219C24.8898 20.7883 25.3625 20.7883 25.4601 21.1219L25.5509 21.4318C25.7679 22.1729 26.3559 22.747 27.1019 22.9462C27.346 23.0114 27.3819 23.3428 27.1574 23.4586L26.95 23.5657C26.2766 23.9133 25.7639 24.5083 25.5196 25.2257L25.3684 25.6699C25.2906 25.8982 24.967 25.8964 24.8917 25.6672L24.7159 25.1316C24.4836 24.4241 23.9704 23.8433 23.2969 23.5256L23.1287 23.4463C22.9005 23.3386 22.9306 23.0049 23.1743 22.9398C23.9055 22.7446 24.4818 22.1819 24.6945 21.4556L24.7922 21.1219Z"
          fill="#464A5E"
        />
      </svg>
    ),
    description: '突破视觉维度，打造极具冲击力的立体呈现',
    cover: 'https://cdn.aitubiao.com/static/images/itubiao/landingpage/hero-3d-chart.png',
    video: 'https://cdn.aitubiao.com/static/videos/hero-3d-chart.mp4',
    className: 'md:col-span-2',
    link: 'https://app.aitubiao.com/home/ai?tab=chart3d'
  }
]

// Card structure
const CardSkeletonBody = ({ children, className }: { children: React.ReactNode; className?: string }) => {
  return <div className={cn('overflow-hidden', className)}>{children}</div>
}

const CardContent = ({ children, className }: { children: React.ReactNode; className?: string }) => {
  return <div className={cn('p-6', className)}>{children}</div>
}

const CardIcon = ({ children, className }: { children: React.ReactNode; className?: string }) => {
  return <div className={cn('h-8 w-8', className)}>{children}</div>
}

const CardTitle = ({ children, className }: { children: React.ReactNode; className?: string }) => {
  return <h2 className={cn('font-sans text-2xl font-medium text-foreground', className)}>{children}</h2>
}

const CardDescription = ({ link, children, className }: { link: string; children: React.ReactNode; className?: string }) => {
  const searchParams = useSearchParams()
  const loginPath = `https://app.aitubiao.com/sign-in?${searchParams.toString()}&redirectURL=${encodeURIComponent(link)}`
  return (
    <div className={cn('max-w-xs space-y-2.5 font-sans text-base font-normal tracking-tight text-neutral-500', className)}>
      <span>{children}</span>
      <Link className="flex items-center gap-2.5 text-primary" href={loginPath} onClick={handleUETClick}>
        <button className="border-none text-sm font-[330] outline-none">即刻体验</button>
        <div className="h-4 w-4">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M14 8C14 7.72386 13.7761 7.5 13.5 7.5H2.49998C2.22383 7.5 1.99998 7.72386 1.99998 8C1.99998 8.27614 2.22383 8.5 2.49998 8.5H13.5C13.7761 8.5 14 8.27614 14 8Z"
              fill="currentColor"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M9.14646 3.64645C8.9512 3.84171 8.95119 4.15829 9.14646 4.35355L12.7929 8L9.14646 11.6464C8.95119 11.8417 8.9512 12.1583 9.14646 12.3536C9.34172 12.5488 9.6583 12.5488 9.85356 12.3536L13.8536 8.35355C14.0488 8.15829 14.0488 7.84171 13.8536 7.64645L9.85356 3.64645C9.6583 3.45118 9.34172 3.45118 9.14646 3.64645Z"
              fill="currentColor"
            />
          </svg>
        </div>
      </Link>
    </div>
  )
}

const Card = ({ children, className }: { children: React.ReactNode; className?: string }) => {
  return (
    <motion.div
      whileHover="animate"
      className={cn(
        'group isolate flex flex-col overflow-hidden rounded-2xl bg-white shadow-[0_1px_1px_rgba(0,0,0,0.05),0_4px_6px_rgba(34,42,53,0.04),0_24px_68px_rgba(47,48,55,0.05),0_2px_3px_rgba(0,0,0,0.04)]',
        className
      )}
    >
      {children}
    </motion.div>
  )
}
